import React, { useState, useRef, useEffect } from "react";
import { isEqual } from "lodash";

import { DayPickerRangeController, isInclusivelyBeforeDay } from "react-dates";

import cx from "classnames";
import { Primary, TextLink } from "../Button";
import { Text } from "../Type";
import { ChevronLeft, ChevronRight } from "../Icons";

import "./overrides.scss";
import moment from "moment";
import { breakpoints } from "../../styles/variables";
import styles from "./index.module.scss";
import DropdownButton from "../Dropdown/DropdownButton";

type DayPickerRangeControllerProps = React.ComponentProps<
  typeof DayPickerRangeController
>;
export type ReactDatesMoment = NonNullable<
  DayPickerRangeControllerProps["startDate"]
>;
type ReactDatesFocusedInput = Exclude<
  DayPickerRangeControllerProps["focusedInput"],
  null | undefined
>;

export type DateRange = Pick<
  DayPickerRangeControllerProps,
  "startDate" | "endDate"
>;

// We're adding a `maxDate` prop and we're changing `focusedInput` and
// `onFocusChange` to be optional because we have a some default behaviors
// that make implementation a little easier/smoother
interface IDayPickerRangeControllerShape
  extends Omit<
    DayPickerRangeControllerProps,
    "focusedInput" | "onFocusChange"
  > {
  maxDate?: ReactDatesMoment;
  focusedInput?: ReactDatesFocusedInput;
  onFocusChange?: (arg: ReactDatesFocusedInput | null) => void;
  rangeSelect?: boolean;
  onApply?: (args: DateRange) => void;
  dateFormat?: string;
  defaultText?: string;
  text?: string;
  clearText?: string;
  hideLabel?: boolean;
  onClearAll?: () => void;
}

// Extend the DayPickerRangeController props type to include maxDate
const ExtendedDayPickerRangeController = DayPickerRangeController as React.ComponentType<
  IDayPickerRangeControllerShape
>;

const DatePicker = ({
  startDate,
  endDate,
  navNext,
  navPrev,
  maxDate,
  numberOfMonths,
  initialVisibleMonth,
  isOutsideRange,
  minimumNights,
  onDatesChange,
  onApply,
  focusedInput,
  onFocusChange,
  rangeSelect,
  dateFormat = "MMM DD, YYYY",
  defaultText = "All",
  text,
  clearText = "Clear All",
  hideLabel,
  onClearAll,
  ...props
}: IDayPickerRangeControllerShape) => {
  const formatDateRange = (
    start: DateRange["startDate"],
    end: DateRange["endDate"]
  ) => {
    let output = String(text || defaultText);

    if (start || end) {
      if (start) {
        output = start.format(dateFormat);
      }
      if (rangeSelect && end && !isEqual(start, end)) {
        output += ` - ${end.format(dateFormat)}`;
      }
    }

    return output;
  };

  const [open, setOpen] = useState(false);

  const [textValue, setTextValue] = useState(
    formatDateRange(startDate, endDate)
  );

  const [focusedDateRangeInput, setFocusedDateRangeInput] = React.useState<
    ReactDatesFocusedInput
  >("startDate");

  const doubleCalendar = 2;
  const isDoubleCalendar =
    (!numberOfMonths || numberOfMonths < doubleCalendar) &&
    window.innerWidth > breakpoints.medium;

  const datePickerDiv = useRef<HTMLDivElement>(null);

  const defaultInitialVisibleMonth: NonNullable<IDayPickerRangeControllerShape["initialVisibleMonth"]> = () =>
    (moment().subtract(
      isDoubleCalendar ? 1 : 0,
      "month"
    ) as unknown) as ReactDatesMoment;

  const defaultOnFocusChange = (focused: ReactDatesFocusedInput | null) => {
    if (rangeSelect) {
      setFocusedDateRangeInput(focused ? focused : "startDate");
    } else {
      setFocusedDateRangeInput("startDate");
    }
  };

  const defaultFocusedInput = !rangeSelect
    ? "startDate"
    : focusedDateRangeInput
    ? focusedDateRangeInput
    : "startDate";

  // Trying to keep the signature the same as `DayPickerRangeController` which uses type `any`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isOutsideRangeDefault = (day: any) =>
    !isInclusivelyBeforeDay(day, (moment() as unknown) as ReactDatesMoment);

  const changeDates = ({ startDate, endDate }: DateRange) => {
    const values = {
      startDate,
      endDate: rangeSelect ? endDate : startDate
    };

    onDatesChange(values);

    if (!onApply) {
      setTextValue(formatDateRange(values.startDate, values.endDate));
    }
  };

  const applyChanges = () => {
    if (onApply) {
      const values = {
        startDate,
        endDate
      };
      onApply(values);
    }
    setOpen(false);
  };

  const handleClearButtonClick = () => {
    changeDates({ startDate: null, endDate: null });
    setFocusedDateRangeInput("startDate");
    setTextValue(defaultText);
    if (onClearAll) onClearAll();
  };

  const handleVisibilityToggle = () => {
    open ? applyChanges() : null;
    setOpen(!open);
  };

  useEffect(() => {
    const clickListener = (e: MouseEvent) => {
      const target = e.target;
      if (
        target &&
        datePickerDiv &&
        datePickerDiv.current &&
        datePickerDiv.current.className.match(styles.FilterOpen) &&
        !datePickerDiv.current.contains(target as Node)
      ) {
        applyChanges();
      }
    };

    const escapeListener = (e: KeyboardEvent) => {
      if (
        datePickerDiv &&
        datePickerDiv.current &&
        datePickerDiv.current.className.match(styles.FilterOpen) &&
        e.key.toLowerCase() === "escape"
      ) {
        applyChanges();
      }
    };

    window.addEventListener("click", clickListener);
    window.addEventListener("keydown", escapeListener);

    return () => {
      window.removeEventListener("click", clickListener);
      window.removeEventListener("keydown", escapeListener);
    };
  });

  useEffect(() => {
    if (startDate && endDate) {
      setTextValue(formatDateRange(startDate, endDate));
    }
    if (!startDate && !endDate) {
      setTextValue("All");
      setFocusedDateRangeInput("startDate");
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  return (
    <div className={styles.DatePickerWrapper}>
      {!hideLabel && (
        <Text size="small" className={styles.DatePickerLabel} component="label">
          Date{`${rangeSelect ? "s" : ""}`}
        </Text>
      )}
      <div
        ref={datePickerDiv}
        className={cx(styles.FilterWrapper, { [styles.FilterOpen]: open })}
      >
        <span className={styles.DatePickerToggle}>
          <DropdownButton
            open={open}
            selectedItem={{ value: textValue, label: textValue }}
            onClick={handleVisibilityToggle}
          />
        </span>
        <div className={cx(styles.Filter, { [styles.FilterOpen]: open })}>
          <div className={styles.Wrapper}>
            <ExtendedDayPickerRangeController
              startDate={startDate}
              endDate={endDate}
              noBorder
              hideKeyboardShortcutsPanel
              focusedInput={focusedInput || defaultFocusedInput}
              onFocusChange={onFocusChange || defaultOnFocusChange}
              minimumNights={minimumNights || 0}
              isOutsideRange={isOutsideRange || isOutsideRangeDefault}
              initialVisibleMonth={
                initialVisibleMonth || defaultInitialVisibleMonth
              }
              numberOfMonths={
                numberOfMonths || isDoubleCalendar ? doubleCalendar : 1
              }
              navNext={
                navNext || (
                  <div className={styles.NavIconWrapperNext}>
                    <ChevronRight />
                  </div>
                )
              }
              navPrev={
                navPrev || (
                  <div className={styles.NavIconWrapperPrev}>
                    <ChevronLeft />
                  </div>
                )
              }
              maxDate={
                maxDate ||
                ((moment().endOf("month") as unknown) as ReactDatesMoment)
              }
              onDatesChange={changeDates}
              {...props}
            />
          </div>
          <div className={styles.Buttons}>
            <TextLink
              onClick={handleClearButtonClick}
              linkStyle="secondary"
              buttonSize="small"
            >
              {clearText}
            </TextLink>
            {onApply && (
              <Primary onClick={applyChanges} buttonSize="small">
                Apply
              </Primary>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
